import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const chain = [
  { label: '单课报告', icon: 'ri-file-list-3-line', desc: '', color: 'primary' as const },
  { label: '多课记录', icon: 'ri-stack-line', desc: '', color: 'accent' as const },
  { label: '教师档案', icon: 'ri-user-line', desc: '', color: 'primary' as const },
  { label: '教研组分析', icon: 'ri-team-line', desc: '', color: 'accent' as const },
  { label: '学校质量图', icon: 'ri-building-line', desc: '', color: 'primary' as const },
  { label: '区域趋势图', icon: 'ri-map-2-line', desc: '', color: 'accent' as const },
];

export default function P69PointLineSurface({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  // Ring layout positions for desktop
  const RADIUS = 200;
  const ARROW_RADIUS = 168;
  const positions = chain.map((_, i) => {
    const angle = -Math.PI / 2 + i * (Math.PI / 3);
    return {
      x: Math.cos(angle) * RADIUS,
      y: Math.sin(angle) * RADIUS,
    };
  });
  const arrowPositions = chain.map((_, i) => {
    const angle = -Math.PI / 2 + i * (Math.PI / 3) + Math.PI / 6;
    return {
      x: Math.cos(angle) * ARROW_RADIUS,
      y: Math.sin(angle) * ARROW_RADIUS,
      rotation: (angle + Math.PI / 2) * (180 / Math.PI),
    };
  });

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-100/12 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-100/10 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-100/8 blur-[150px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-14 md:pt-16 pb-8 md:pb-12">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-5 md:mb-6">
          <span className={`inline-flex items-center gap-2 text-2xl font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-2 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />数据递进链
          </span>
          <h2 className={`font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-foreground-950 mb-2 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            从<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">一次评课</span>到<span className="text-gold-600">连续质量数据</span>
          </h2>
          <p className={`text-xl md:text-2xl text-foreground-950 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            单课评价提供事实，连续数据提供变化，逐级汇总才形成跟踪依据
          </p>
        </div>

        {/* Ring Layout (desktop) */}
        <div className={`hidden md:block relative mx-auto mb-5 md:mb-6 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="relative w-[560px] h-[560px] lg:w-[600px] lg:h-[600px] mx-auto">
            <div className="absolute inset-10 lg:inset-12 rounded-full border-2 border-dashed border-background-200/60" />

            {/* Center core */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="w-[96px] h-[96px] lg:w-[104px] lg:h-[104px] rounded-full glass-gold border border-gold-200/40 flex flex-col items-center justify-center gap-0.5">
                <i className="ri-database-2-line text-3xl lg:text-4xl text-gold-600" />
                <span className="text-lg lg:text-xl font-body font-bold text-gold-700 leading-tight">逐级汇聚</span>
              </div>
            </div>

            {chain.map((item, i) => (
              <div
                key={item.label}
                className="absolute top-1/2 left-1/2"
                style={{
                  transform: `translate(calc(-50% + ${positions[i].x}px), calc(-50% + ${positions[i].y}px))`,
                  transitionDelay: `${300 + i * 120}ms`,
                }}
              >
                <div className="flex items-center gap-2 px-3 py-2.5 glass-liquid rounded-xl">
                  <div className={`w-11 h-11 lg:w-12 lg:h-12 rounded-lg ${item.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : 'bg-primary-100/60 border border-primary-200/40'} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${item.icon} text-xl lg:text-2xl ${item.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-lg font-mono font-bold text-foreground-950 leading-none mb-0.5">{String(i + 1).padStart(2, '0')}</span>
                    <span className="text-xl lg:text-2xl font-body font-bold text-foreground-950 whitespace-nowrap leading-tight">{item.label}</span>
                  </div>
                </div>
              </div>
            ))}

            {arrowPositions.map((pos, i) => (
              <div
                key={`arrow-${i}`}
                className="absolute top-1/2 left-1/2"
                style={{
                  transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px)) rotate(${pos.rotation}deg)`,
                }}
              >
                <div className="w-7 h-7 rounded-full bg-background-100 border border-background-200/50 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-lg text-foreground-950" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile: vertical flow */}
        <div className={`md:hidden max-w-md mx-auto w-full mb-5 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="flex flex-col items-center gap-1">
            {chain.map((c, i) => (
              <div key={c.label} className="flex flex-col items-center w-full">
                <div className="w-full p-4 bg-background-50 border border-background-200/70 rounded-xl flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl ${c.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : 'bg-primary-100/60 border border-primary-200/40'} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${c.icon} text-xl ${c.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`} />
                  </div>
                  <div className="flex-1">
                    <span className="text-lg font-mono font-bold text-foreground-950 mr-2">{String(i + 1).padStart(2, '0')}</span>
                    <span className="text-xl font-body font-bold text-foreground-950">{c.label}</span>
                  </div>
                </div>
                {i < chain.length - 1 && (
                  <i className="ri-arrow-down-line text-xl text-foreground-950 my-1" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <div className={`max-w-4xl mx-auto transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-7 md:p-8 glass-liquid rounded-xl text-center">
            <p className="text-xl md:text-2xl text-foreground-950 font-body leading-relaxed">
              连续汇总不是把所有课程简单求平均。教师层面跟踪同类维度变化，学校层面则<strong className="text-gold-700">先按课程进行专业评价</strong>，再对互动、节奏、资源使用等共同维度进行跨课程分析。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}